import { defineStore } from 'pinia'

import {
    getVerificationData,
    createVerification,
    getVerificationStatus,
    getVerificationCardStatus,
    updateVerificationBilling,
    updateVerificationKYC,
    updateVerificationCard,
    getStripeUrl,
} from '@/api/verification-routes'

import { useDefaultStore } from '@/store/defaultStore'

import { BILLING_TYPE } from '@/constants/verification'
import { STRIPE_URL_TYPES } from '@/constants/stripeUrlTypes'

export const useVerificationUserStore = defineStore('verificationUserStore', {
    state: () => ({
        loading: false,
        actionLoading: false,

        billingStepDone: false,
        kycStepDone: false,
        paymentStepDone: false,

        idenfyIsVerify: false,
        stripeIsVerify: false,

        scan_ref: null,

        verificationSteps: {},

        billingStepFormData: {
            billing_type_id: BILLING_TYPE.INDIVIDUAL,
        },

        kyc_files: {
            certificates: [],
            business_address: [],
        },

        card_files: {
            front_card: [],
            back_card: [],
        },

        idenfyUrl: null,
        idenfyStatus: null,

        stripeUrl: null,
        stripeStatus: null,

        kycStepStatus: null,
        paymentStepStatus: null,

        reviewStep: null,
    }),

    getters: {
        billingTypeIsBusiness({ billingStepFormData }) {
            return billingStepFormData.billing_type_id === BILLING_TYPE.BUSINESS
        },
    },

    actions: {
        parsPayload(account) {
            this.setVerification(account)

            this.setStepProggers(account)
            this.setBillingFormData(account)
        },

        setVerification(account) {
            const { verification } = account

            if (!verification) {
                return
            }

            this.verificationSteps = verification
        },

        setStepProggers(account) {
            if (!account?.verification) {
                return
            }

            this.scan_ref = account.scan_ref

            this.billingStepDone = account.verification.billingStep?.done
            this.kycStepDone = account.verification.kycStep?.done
            this.paymentStepDone = account.verification.paymentStep?.done
            this.kycStepStatus = account.verification.kycStep?.status
            this.paymentStepStatus = account.verification.paymentStep?.status

            this.setIdenfyIsVerify(account.verification.kycStep?.idenfyDone)
            this.setStripeIsVerify(account.verification.paymentStep?.stripeDone)
        },

        setIdenfyIsVerify(value) {
            this.idenfyIsVerify = value
        },

        setStripeIsVerify(value) {
            this.stripeIsVerify = value
        },

        setBillingFormData(formData) {
            this.billingStepFormData.billing_type_id = formData.billing_type_id || BILLING_TYPE.INDIVIDUAL
        },

        updateKycFiles(key, data) {
            this.kyc_files[key] = data
        },

        updateCardFiles(key, data) {
            this.card_files[key] = data
        },

        setReviewStep(step) {
            this.reviewStep = step
        },

        async goToLoadVerificationData() {
            this.loading = true

            const { success, message, payload } = await getVerificationData()

            if (success) {
                this.parsPayload(payload.account)
            } else {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
            }

            this.loading = false

            return { success }
        },

        async updateVerificationDataBilling() {
            const defaultStore = useDefaultStore()

            const { success, payload, message } = await updateVerificationBilling(this.billingStepFormData)

            if (success) {
                this.parsPayload(payload)
                defaultStore.setErrorMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            return { success }
        },

        async updateVerificationDataKYC() {
            const defaultStore = useDefaultStore()

            this.actionLoading = true

            const files = new FormData()

            Object.entries(this.kyc_files).forEach(([key, fileList]) => {
                fileList.forEach((file) => {
                    files.append(`${key}[]`, file, file.name)
                })
            })

            const { success, payload, message } = await updateVerificationKYC({
                formData: files,
                config: {
                    headers: { 'Content-Type': 'multipart/form-data' },
                },
            })

            if (success) {
                this.updateKycFiles('certificates', [])
                this.updateKycFiles('business_address', [])
                this.parsPayload(payload)

                defaultStore.setErrorMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false

            return { success }
        },

        async updateVerificationDataCard() {
            this.actionLoading = true

            const formData = new FormData()

            Object.entries(this.card_files).forEach(([key, fileList]) => {
                fileList.forEach((file) => {
                    formData.append(`${key}[]`, file, file.name)
                })
            })

            const { success, payload, message } = await updateVerificationCard({
                formData,
                config: {
                    headers: { 'Content-Type': 'multipart/form-data' },
                },
            })

            if (success) {
                this.updateCardFiles('front_card', [])
                this.updateCardFiles('back_card', [])

                this.parsPayload(payload)
            } else {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false

            return { success }
        },

        async goToVerificationIdenfy() {
            const { success, payload, message } = await createVerification()

            if (success) {
                this.idenfyUrl = payload
            } else {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
            }

            return { success }
        },

        async goToStripeVerification() {
            const { success, payload, message } = await getStripeUrl(STRIPE_URL_TYPES.verification)

            if (success) {
                this.stripeUrl = payload
            } else {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
            }

            return { success }
        },

        async goToCheckVerificationIdenfyStatus() {
            const { success, payload, message } = await getVerificationStatus()

            if (success) {
                this.idenfyStatus = payload
            } else {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
            }
        },

        async goToCheckVerificationCardStatus() {
            const { success, payload, message } = await getVerificationCardStatus()

            if (success) {
                this.stripeStatus = payload
            } else {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
            }
        },
    },
})
